// Créé · En route · En livraison · Livré (7 j) — one chip per parcel
// (owner, 2026-10-10). Problem chips are red with a timer, on top of their
// lane. When AMEEX moves a parcel the chip slides to its new lane (FLIP:
// measured before and after, animated with transform only). Each lane
// scrolls on its own; the page never grows. Phone: one lane at a time, swipe.
import { useLayoutEffect, useRef, useState } from 'react';
import { useT } from '@/lib/i18n';
import type { AmeexBoardParcel, AmeexLane } from '@/lib/api';
import { LANES, LANE_LABEL, orderLane, problemShort, since } from './board';

function Chip({ p, now, selected, onSelect, refFor, big }: {
  p: AmeexBoardParcel; now: number; selected: boolean; onSelect: (p: AmeexBoardParcel) => void;
  refFor: (code: string) => (el: HTMLButtonElement | null) => void; big?: boolean;
}) {
  const { t } = useT();
  const red = Boolean(p.problem);
  const word = problemShort(p, t);
  const tone = red
    ? 'border-status-stuck-border bg-status-stuck-bg text-status-stuck'
    : p.lane === 'livre' ? 'border-border-subtle bg-bg-card text-text-primary'
      : 'border-border-subtle bg-bg-card text-text-primary';
  return (
    <button
      type="button"
      ref={refFor(p.code)}
      onClick={() => onSelect(p)}
      aria-pressed={selected}
      aria-label={`${p.name ?? t('Sans nom')} · ${p.city ?? ''}${word ? ` — ${word}` : ''}`}
      className={`w-full text-left rounded-xl border cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${big ? 'px-3.5 py-3 min-h-[48px]' : 'px-3 py-2'} ${tone} ${selected ? 'ring-2 ring-brand-500' : 'hover:bg-bg-elevated'}`}
    >
      <span className="flex items-center gap-2">
        {red && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
        <span className={`min-w-0 flex-1 truncate ${big ? 'text-[15px]' : 'text-[13.5px]'} ${red ? 'font-medium' : ''}`}>
          {p.name ?? t('Sans nom')}<span className={red ? '' : 'text-text-tertiary'}> · {p.city ?? '—'}</span>
        </span>
        {red && <span className="shrink-0 text-[11.5px] font-semibold tabular-nums">{since(p.lastEventAt, now)}</span>}
      </span>
      {word && <span className="mt-0.5 block text-[11.5px] opacity-90">{word}</span>}
    </button>
  );
}

export function DeliveryBoard({ parcels, now, selected, onSelect, isPhone }: {
  parcels: AmeexBoardParcel[]; now: number; selected: string | null; onSelect: (p: AmeexBoardParcel) => void; isPhone: boolean;
}) {
  const { t } = useT();
  const byLane = new Map<AmeexLane, AmeexBoardParcel[]>(LANES.map((l) => [l, []]));
  for (const p of parcels) byLane.get(p.lane)?.push(p);
  for (const l of LANES) byLane.set(l, orderLane(byLane.get(l)!));

  // ─── FLIP: a chip that changed lane slides from where it was ───────────
  const nodes = useRef(new Map<string, HTMLButtonElement>());
  const before = useRef(new Map<string, { rect: DOMRect; lane: AmeexLane }>());
  const refFor = (code: string) => (el: HTMLButtonElement | null) => {
    if (el) nodes.current.set(code, el); else nodes.current.delete(code);
  };
  const laneOf = new Map(parcels.map((p) => [p.code, p.lane]));
  useLayoutEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    for (const [code, el] of nodes.current) {
      const prev = before.current.get(code);
      const lane = laneOf.get(code);
      if (!prev || !lane || prev.lane === lane) continue;
      el.classList.add('jm-lit');
      window.setTimeout(() => el.classList.remove('jm-lit'), 2300);
      if (reduce || typeof el.animate !== 'function') continue;
      const r = el.getBoundingClientRect();
      const dx = prev.rect.left - r.left, dy = prev.rect.top - r.top;
      if (Math.abs(dx) + Math.abs(dy) < 2) continue;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
        { duration: 650, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
    }
    const next = new Map<string, { rect: DOMRect; lane: AmeexLane }>();
    for (const [code, el] of nodes.current) {
      const lane = laneOf.get(code);
      if (lane) next.set(code, { rect: el.getBoundingClientRect(), lane });
    }
    before.current = next;
  });

  const [phoneLane, setPhoneLane] = useState<AmeexLane>('livraison');
  const touch = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);
  const step = (d: number) => {
    const i = LANES.indexOf(phoneLane);
    setPhoneLane(LANES[Math.min(LANES.length - 1, Math.max(0, i + d))]);
  };
  const empty = <p className="px-1 py-4 text-[12.5px] text-text-tertiary">{t('Aucun colis ici.')}</p>;

  if (isPhone) {
    const list = byLane.get(phoneLane)!;
    return (
      <section aria-label={t('Colis')}>
        <div role="tablist" aria-label={t('Étapes')} className="flex gap-1.5 overflow-x-auto pb-2">
          {LANES.map((l) => {
            const n = byLane.get(l)!.length;
            const red = byLane.get(l)!.some((p) => p.problem);
            return (
              <button key={l} type="button" role="tab" aria-selected={phoneLane === l} onClick={() => setPhoneLane(l)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 min-h-[40px] text-[13px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${phoneLane === l ? 'border-brand-500 bg-brand-500/15 text-text-primary font-semibold' : 'border-border-subtle bg-bg-card text-text-secondary'}`}>
                {t(LANE_LABEL[l])}
                <span className={`tabular-nums text-[12px] ${red ? 'text-status-stuck font-semibold' : 'text-text-tertiary'}`}>{n}</span>
              </button>
            );
          })}
        </div>
        <div
          className="h-[50dvh] overflow-y-auto rounded-2xl border border-border-subtle bg-bg-primary p-2"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={(e) => { const p = e.touches[0]; touch.current = { x: p.clientX, y: p.clientY, axis: null }; }}
          onTouchMove={(e) => {
            const s = touch.current; if (!s || s.axis) return;
            const p = e.touches[0]; const dx = p.clientX - s.x; const dy = p.clientY - s.y;
            if (Math.abs(dx) > 12 || Math.abs(dy) > 12) s.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'x' : 'y';
          }}
          onTouchEnd={(e) => {
            const s = touch.current; touch.current = null;
            if (!s || s.axis !== 'x' || s.x < 24) return;
            const dx = e.changedTouches[0].clientX - s.x;
            if (Math.abs(dx) > 60) step(dx < 0 ? 1 : -1);
          }}
        >
          {list.length ? (
            <ul className="flex flex-col gap-2">
              {list.map((p) => <li key={p.code}><Chip p={p} now={now} selected={selected === p.code} onSelect={onSelect} refFor={refFor} big /></li>)}
            </ul>
          ) : empty}
        </div>
        <div className="flex justify-center gap-1.5 pt-2" aria-hidden>
          {LANES.map((l) => <span key={l} className={`h-1.5 w-1.5 rounded-full ${l === phoneLane ? 'bg-brand-500' : 'bg-border-subtle'}`} />)}
        </div>
      </section>
    );
  }

  return (
    <section aria-label={t('Colis')} className="grid grid-cols-4 gap-3 items-start">
      {LANES.map((l) => {
        const list = byLane.get(l)!;
        const red = list.filter((p) => p.problem).length;
        return (
          <div key={l} className="flex flex-col min-h-0 max-h-[380px]">
            <h3 className="px-1 pb-2 text-[14px] text-text-secondary">
              {t(LANE_LABEL[l])} <span className="tabular-nums">· {list.length}</span>
              {red > 0 && <span className="ml-1.5 text-[12px] font-semibold text-status-stuck tabular-nums">{t('{n} en problème', { n: red })}</span>}
            </h3>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              {list.length ? (
                <ul className="flex flex-col gap-2">
                  {list.map((p) => <li key={p.code}><Chip p={p} now={now} selected={selected === p.code} onSelect={onSelect} refFor={refFor} /></li>)}
                </ul>
              ) : empty}
            </div>
          </div>
        );
      })}
    </section>
  );
}
